import { ClaimRecordStatus } from './ClaimRecord'
import { InsurancePlan } from './InsurancePlan'
import { Price } from './Price'
import { QuoteData, QuoteStatusSummary } from './QuoteData'

export const enum ClaimStatus {
  CREATED = 'CREATED',
  CLAIM_SUBMITTED = 'CLAIM_SUBMITTED',
  CLAIM_SUBMIT_TIMEOUT = 'CLAIM_SUBMIT_TIMEOUT',
  CLAIM_RETRY = 'CLAIM_RETRY',
  CLAIM_RETRY_FAILED = 'CLAIM_RETRY_FAILED',
  CLAIM_ACCEPTED = 'CLAIM_ACCEPTED',
  CLAIM_REJECTED = 'CLAIM_REJECTED',
  CLAIM_ACKNOWLEDGED = 'CLAIM_ACKNOWLEDGED',
  CLAIM_ERROR = 'CLAIM_ERROR',
  CLAIM_REFUNDED = 'CLAIM_REFUNDED',
  CLAIM_INVOICED = 'CLAIM_INVOICED',
  NULLIFY_SUBMITTED = 'NULLIFY_SUBMITTED',
  NULLIFY_ACCEPTED = 'NULLIFY_ACCEPTED',
  NULLIFY_REJECTED = 'NULLIFY_REJECTED',
  NULLIFY_ERROR = 'NULLIFY_ERROR',
  FOR_MANUAL_NULLIFY = 'FOR_MANUAL_NULLIFY',
  CLAIM_NULLIFIED_MANUALLY = 'CLAIM_NULLIFIED_MANUALLY',
  DELETED = 'DELETED',
  CLOSED = 'CLOSED',
  NONE = 'NONE',
}

export const enum CustomerFacingClaimStatus {
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  RETRY = 'RETRY',
  ERROR = 'ERROR',
  NULLIFY_IN_PROGRESS = 'NULLIFY_IN_PROGRESS',
  UNKNOWN = 'UNKNOWN',
  NONE = 'NONE',
}

export const enum SubmitClaimResponseStatus {
  CLAIM_ACCEPTED = 'CLAIM_ACCEPTED',
  CLAIM_REJECTED = 'CLAIM_REJECTED',
  CLAIM_ACKNOWLEDGED = 'CLAIM_ACKNOWLEDGED',
  CLAIM_SUBMIT_TIMEOUT = 'CLAIM_SUBMIT_TIMEOUT',
  CLAIM_ERROR = 'CLAIM_ERROR',
  NONE = 'NONE',
}

export const enum FrontEndAction {
  COLLECT_CLAIM_DATA = 'COLLECT_CLAIM_DATA',
  POLLING = 'POLLING',
  ACCEPTED_MSG = 'ACCEPTED_MSG',
  ACKNOWLEDGED_MSG = 'ACKNOWLEDGED_MSG',
  REJECTED_MSG = 'REJECTED_MSG',
  ERROR_MSG = 'ERROR_MSG',
  CANCELLED_MSG = 'CANCELLED_MSG',
}

export type ClaimStatusSummary = {
  lastClaim?: boolean
  claimId?: string
  status: ClaimStatus
  frontEndAction?: FrontEndAction
  customerFacingStatus?: CustomerFacingClaimStatus
  submitResponseStatus?: SubmitClaimResponseStatus
  claimRecordStatus?: ClaimRecordStatus
  errorType?: ClaimErrorType
  errorCode?: ClaimErrorCode
  errorMessage?: string
}

export type AccidentFlag = 'Y' | 'N'

export const enum InsuranceUsage {
  INSURANCE_ENABLED = 'INSURANCE_ENABLED',
  INSURANCE_DISABLED = 'INSURANCE_DISABLED',
  INSURANCE_NOT_APPLICABLE = 'INSURANCE_NOT_APPLICABLE',
  INSURANCE_ENABLED_ELIGIBILITY_CHECK_REQUIRED = 'INSURANCE_ENABLED_ELIGIBILITY_CHECK_REQUIRED',
  INSURANCE_DISABLED_ELIGIBILITY_CHECK_REQUIRED = 'INSURANCE_DISABLED_ELIGIBILITY_CHECK_REQUIRED',
}

export const enum InsuranceStatusErrorCode {
  NONE = 'NONE',
  ELIGIBLE_PRODUCTS_REQUIRED = 'ELIGIBLE_PRODUCTS_REQUIRED',
  BENEFITS_UNAVAILABLE = 'BENEFITS_UNAVAILABLE',
  CART_QUOTE_FAILED = 'CART_QUOTE_FAILED',
  CART_QUOTE_IN_PROGRESS = 'CART_QUOTE_IN_PROGRESS',
  CLAIM_SUBMIT_IN_PROGRESS = 'CLAIM_SUBMIT_IN_PROGRESS',
  CLAIM_NULLIFY_IN_PROGRESS = 'CLAIM_NULLIFY_IN_PROGRESS',
  CLAIM_SUBMISSION_ERROR = 'CLAIM_SUBMISSION_ERROR',
  PRODUCT_QUOTE_FAILED = 'PRODUCT_QUOTE_FAILED',
  CONTACT_LENSES_COMING_SOON = 'CONTACT_LENSES_COMING_SOON',
}

export type ClaimData = {
  claim?: Claim
  patient?: Patient
  primaryCoverage?: PolicyCoverage
  secondaryCoverage?: PolicyCoverage
  termsAndConditionsAccepted?: boolean
  provincialPlanExhausted?: boolean
  isAccident?: AccidentFlag // not sent to B/E
  accident?: Accident
  referrer?: Referrer
  quote?: QuoteData
  quoteSummaries?: QuoteStatusSummary[]
  claimSummaries?: ClaimStatusSummary[]
  useInsuranceStatus?: InsuranceUsage
  insuranceStatusErrorCode?: InsuranceStatusErrorCode
  refreshCartTotalRequired?: boolean
  showDistributeInsuranceBenefits?: boolean
}

export type Referrer = {
  type?: string
  firstName?: string
  lastName?: string
}

export type Accident = {
  date?: string
  type?: string
}

export type Patient = {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  gender?: string
  nationalIdentifier?: string
  birthRank?: string
  dateOfPrescription?: string
  phoneNumber?: string
  emailAddress?: string
  postalCode?: string
}

export type PolicyCoverage = {
  insurer?: PolicyInsurer
  insuree?: Insuree
  type?: string
  patientRelationship?: string
}

export type PolicyInsurer = {
  id?: string
  name?: string
  insuranceProviderId?: string
  insuranceProviderName?: string
}

export type Insuree = {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  policyNumber?: string
  groupId?: string
  memberId?: string
  memberType?: string
  insuranceType?: InsuranceType | string
  contractId?: string
  insurancePlans?: InsurancePlan[]
}

export const enum InsuranceType {
  ADDITIONAL_INSURANCE = 'ADDITIONAL_INSURANCE',
  NATIONAL_INSURANCE = 'NATIONAL_INSURANCE',
  SPECIAL_CONDITION_INSURANCE = 'SPECIAL_CONDITION_INSURANCE',
  REGIONAL_INSURANCE = 'REGIONAL_INSURANCE',
  REGIONAL_SPECIAL_CONDITION_INSURANCE = 'REGIONAL_SPECIAL_CONDITION_INSURANCE',
  MATERNITY_INSURANCE = 'MATERNITY_INSURANCE',
  WORK_ACCIDENT_INSURANCE = 'WORK_ACCIDENT_INSURANCE',
}

export type Claim = {
  claimId: string
  insurerClaimId: string
  payee: PayeeType
  status: ClaimStatus
  confirmedAmount: Price
  requestedAmount: Price
  orderSubtotal: Price
  shippingCosts: Price
  coveredAmountAfterRefunds: Price
  orderNumber?: string
  customerFacingStatus?: CustomerFacingClaimStatus
  errorType?: ClaimErrorType
  errorCode?: ClaimErrorCode
  errorMessage?: string
}

export const enum ClaimErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CUSTOMER_FACING_ERROR = 'CUSTOMER_FACING_ERROR',
  TECHNICAL_ERROR = 'TECHNICAL_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  COMMUNICATION_ERROR = 'COMMUNICATION_ERROR',
}

export const enum ClaimErrorCode {
  BENEFICIARY_NOT_FOUND = 'BENEFICIARY_NOT_FOUND',
  INSURER_OFFLINE = 'INSURER_OFFLINE',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export const enum PayeeType {
  PROVIDER = 'PROVIDER',
  PROVIDER_ORGANIZATION = 'PROVIDER_ORGANIZATION',
  POLICY_HOLDER = 'POLICY_HOLDER',
  PATIENT = 'PATIENT',
  GUARANTOR = 'GUARANTOR',
  NONE = 'NONE',
}
